import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import RichTextEditor from '../RichTextEditor';
import ConfirmModal from './ConfirmModal';
import { colors, typography } from '../../theme/designSystem';
import { getDefaultInputLabelProps, getDefaultTextFieldSx } from '../../theme/textFieldStyles';
import { PatientDocumentTypeRow, patientDocumentsService } from '../../services/patientDocumentsService';

/**
 * Emissão/edição de documento do paciente (aba Documentos, item 64 + item 122). Os tipos vêm do
 * catálogo da entidade (Administrativo → Tipos de documento); ao escolher um tipo com TEXTO
 * PADRÃO, o editor é preenchido para o profissional complementar e emitir. Com texto já escrito,
 * o modal pergunta antes de substituir — nunca sobrescreve em silêncio.
 *
 * Variáveis do texto padrão (RN-537s) são trocadas pela API ao escolher o tipo, com a data do campo
 * Data. Trocar a data depois troca no texto os valores que dependem dela (data, idade, atendimento
 * do dia), sem mexer no resto do que foi editado.
 */

/** No tipo Personalizado o título é o que o profissional escrever no editor; a impressão não põe cabeçalho de tipo. */
export const CUSTOM_KIND = 'Personalizado';

export interface DocumentDraft {
  id?: string;
  kind: string;
  documentTypeId: string | null;
  date: string;
  issuer: string;
  content: string;
}

interface Props {
  open: boolean;
  patientId: string;
  initial: DocumentDraft | null;
  defaultIssuer: string;
  types: PatientDocumentTypeRow[];
  saving?: boolean;
  onClose: () => void;
  onSave: (draft: DocumentDraft) => void;
}

const today = () => new Date().toISOString().slice(0, 10);

const plainText = (html: string) => html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();

const escapeHtml = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const BLANK = '______';

// Só o que depende da data é retrocado quando ela muda; o resto do texto é do profissional
const DATE_DEPENDENT = ['documento.data', 'documento.data_extenso', 'paciente.idade', 'atendimento.inicio', 'atendimento.fim', 'atendimento.servico', 'atendimento.profissional'];

/** Texto padrão preenchido: guarda o original e os valores usados, para retrocar ao mudar a data. */
interface Rendered {
  template: string;
  values: Record<string, string>;
}

const DocumentModal: React.FC<Props> = ({ open, patientId, initial, defaultIssuer, types, saving = false, onClose, onSave }) => {
  // '' = Personalizado (tipo do sistema, sem corpo)
  const [typeId, setTypeId] = useState<string>('');
  const [date, setDate] = useState(today());
  const [issuer, setIssuer] = useState(defaultIssuer);
  const [content, setContent] = useState('');
  const [error, setError] = useState('');
  // Troca de tipo com texto já escrito: o corpo padrão do novo tipo espera confirmação
  const [pendingType, setPendingType] = useState<PatientDocumentTypeRow | null>(null);
  // O editor inicializa uma vez; para injetar o texto padrão ele é remontado por esta chave
  const [editorKey, setEditorKey] = useState(0);
  const [rendered, setRendered] = useState<Rendered | null>(null);
  const [filling, setFilling] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTypeId(initial?.documentTypeId ?? '');
    setDate(initial?.date ?? today());
    setIssuer(initial?.issuer ?? defaultIssuer);
    setContent(initial?.content ?? '');
    setError('');
    setPendingType(null);
    setRendered(null);
    setEditorKey((k) => k + 1);
  }, [open, initial, defaultIssuer]);

  const replaceEditorContent = (html: string) => {
    setContent(html);
    setEditorKey((k) => k + 1);
  };

  const applyDefault = async (type: PatientDocumentTypeRow | null) => {
    setTypeId(type?.id ?? '');
    setRendered(null);
    if (!type?.defaultContent) return;
    setFilling(true);
    try {
      const result = await patientDocumentsService.render(patientId, type.defaultContent, date);
      replaceEditorContent(result.content);
      setRendered({ template: type.defaultContent, values: result.values });
    } catch {
      // Sem a API o texto padrão entra cru: as variáveis ficam visíveis para completar à mão
      replaceEditorContent(type.defaultContent);
      setError('Não foi possível preencher as variáveis do texto padrão. Complete os campos à mão.');
    } finally {
      setFilling(false);
    }
  };

  const changeDate = async (newDate: string) => {
    setDate(newDate);
    if (!rendered || !newDate) return;
    try {
      const result = await patientDocumentsService.render(patientId, rendered.template, newDate);
      let html = content;
      DATE_DEPENDENT.forEach((key) => {
        const before = rendered.values[key];
        const after = result.values[key];
        // Valor que antes não existia saiu como lacuna: não há como saber qual lacuna era dele
        if (!before || before === after) return;
        html = html.split(escapeHtml(before)).join(after ? escapeHtml(after) : BLANK);
      });
      if (html !== content) replaceEditorContent(html);
      setRendered({ template: rendered.template, values: result.values });
    } catch {
      setError('Não foi possível atualizar o texto com a nova data. Confira as datas e horários no conteúdo.');
    }
  };

  const chooseType = (id: string) => {
    const type = types.find((t) => t.id === id) ?? null;
    if (type?.defaultContent && plainText(content)) {
      setPendingType(type);
      return;
    }
    applyDefault(type);
  };

  const kind = typeId ? (types.find((t) => t.id === typeId)?.name ?? initial?.kind ?? CUSTOM_KIND) : CUSTOM_KIND;

  const save = () => {
    if (!plainText(content)) { setError('Escreva o conteúdo do documento.'); return; }
    onSave({ id: initial?.id, kind, documentTypeId: typeId || null, date, issuer: issuer.trim(), content });
  };

  // Documento antigo de um tipo que foi inativado continua mostrando o tipo dele no seletor
  const typeOptions = initial?.documentTypeId && !types.some((t) => t.id === initial.documentTypeId)
    ? [...types, { id: initial.documentTypeId, name: initial.kind, code: null, defaultContent: null, order: 999, active: false, documentsCount: 0 }]
    : types;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem' }}>
        <Typography component="span" sx={{ fontSize: '1.4rem', fontWeight: typography.fontWeight.semibold }}>
          {initial?.id ? 'Editar documento' : 'Emitir documento'}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: colors.white, padding: '0.25rem' }} aria-label="Fechar">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: '1.5rem !important', paddingTop: '1.5rem !important' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 180px 1fr' }, columnGap: 1.5, rowGap: 2, mb: 2 }}>
          <TextField
            select
            size="small"
            label="Tipo de documento"
            value={typeId}
            onChange={(e) => chooseType(e.target.value)}
            InputLabelProps={getDefaultInputLabelProps()}
            sx={getDefaultTextFieldSx()}
          >
            {typeOptions.map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.name}{t.defaultContent ? ' · com texto padrão' : ''}</MenuItem>
            ))}
            <MenuItem value="">{CUSTOM_KIND}</MenuItem>
          </TextField>
          <TextField
            size="small"
            type="date"
            label="Data"
            value={date}
            onChange={(e) => changeDate(e.target.value)}
            InputLabelProps={getDefaultInputLabelProps()}
            sx={getDefaultTextFieldSx()}
          />
          {/* Quem emite é quem está logado: o documento tem autoria, não se assina por outro */}
          <TextField
            size="small"
            label="Emitido por"
            value={issuer}
            disabled
            InputLabelProps={getDefaultInputLabelProps()}
            sx={getDefaultTextFieldSx()}
          />
        </Box>

        <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary, mb: 0.75 }}>
          {filling
            ? 'Preenchendo os dados do paciente...'
            : kind === CUSTOM_KIND ? 'Conteúdo — comece pelo título do documento; ele não é impresso automaticamente' : 'Conteúdo — confira e complete o texto antes de emitir; tudo pode ser editado'}
        </Typography>
        <RichTextEditor
          key={`doc-${initial?.id ?? 'new'}-${editorKey}`}
          value={content}
          onChange={setContent}
          placeholder="Escreva o documento como ele deve sair no papel..."
        />

        {error && (
          <Typography sx={{ color: colors.error, fontSize: '0.85rem', mt: 1.5 }}>{error}</Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: '1rem 1.5rem', gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={saving} sx={{ textTransform: 'none', borderColor: colors.border, color: colors.textSecondary }}>
          Cancelar
        </Button>
        <Button onClick={save} variant="contained" disabled={saving || filling} sx={{ textTransform: 'none', backgroundColor: colors.primary, '&:hover': { backgroundColor: colors.primaryHover } }}>
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>

      <ConfirmModal
        isOpen={pendingType !== null}
        title="Substituir o conteúdo?"
        message={`O tipo "${pendingType?.name ?? ''}" tem um texto padrão. Substituir o que já está escrito pelo texto padrão? O texto atual será perdido.`}
        type="warning"
        confirmButtonText="Substituir"
        cancelButtonText="Manter o texto"
        onConfirm={() => { applyDefault(pendingType); setPendingType(null); }}
        onCancel={() => { setTypeId(pendingType?.id ?? ''); setPendingType(null); }}
      />
    </Dialog>
  );
};

export default DocumentModal;
